<template>
  <div class="copy-product-option-modal">
    <p class="copy-product-option-modal__intro">
      {{ $t('products.copy_options.intro') }}
    </p>

    <SelectInput
      id="copy-options-source-product"
      :label="$t('products.copy_options.source_product')"
      v-model="selectedProductId"
      :options="productOptions"
    />

    <div v-if="loadingProduct" class="copy-product-option-modal__loading">
      <LoadingSpinner />
      <span>{{ $t('products.copy_options.loading') }}</span>
    </div>

    <template v-else-if="selectedProductId && sourceProduct">
      <p v-if="!sourceOptions.length" class="copy-product-option-modal__hint">
        {{ $t('products.copy_options.no_options') }}
      </p>

      <div v-else class="copy-product-option-modal__selection">
        <div class="copy-product-option-modal__toolbar">
          <button type="button" class="copy-product-option-modal__link" @click="selectAll">
            {{ $t('products.copy_options.select_all') }}
          </button>
          <button type="button" class="copy-product-option-modal__link" @click="clearSelection">
            {{ $t('products.copy_options.clear_selection') }}
          </button>
          <span class="copy-product-option-modal__count">
            {{ selectedCountLabel }}
          </span>
        </div>

        <div
          v-for="group in groupedOptions"
          :key="group.id || 'ungrouped'"
          class="copy-product-option-modal__group"
        >
          <label class="copy-product-option-modal__group-header">
            <input
              type="checkbox"
              :checked="isGroupFullySelected(group)"
              :indeterminate.prop="isGroupPartiallySelected(group)"
              @change="toggleGroup(group, $event.target.checked)"
            />
            <span class="copy-product-option-modal__group-name">
              {{ group.name }}
            </span>
            <span class="copy-product-option-modal__group-count">
              {{ group.options.length }}
            </span>
          </label>

          <div class="copy-product-option-modal__options">
            <label
              v-for="option in group.options"
              :key="option.id"
              class="copy-product-option-modal__option"
            >
              <input
                type="checkbox"
                :checked="selectedOptionIds.includes(option.id)"
                @change="toggleOption(option.id, $event.target.checked)"
              />
              <span class="copy-product-option-modal__option-label">
                {{ option.label || option.id }}
              </span>
              <span class="copy-product-option-modal__option-type">
                {{ option.type }}
              </span>
              <span
                v-if="option.choiceConfig?.choices?.length"
                class="copy-product-option-modal__option-meta"
              >
                {{
                  $t('products.copy_options.choice_count', {
                    n: option.choiceConfig.choices.length,
                  })
                }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </template>

    <div class="modal__actions">
      <Btn
        :text="$t('common.cancel')"
        variant="outlined"
        :disabled="submitting"
        @click="handleCancel"
      />
      <Btn
        :text="submitLabel"
        variant="primary"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, getCurrentInstance } from 'vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';
  import { useUIStore } from '@/store/uiStore';
  import { getProductById } from '@shared/services/productService';
  import {
    copyProductOptions,
    resolveSelectedOptionIds,
  } from '@shared/utils/copyProductOptions';

  export default {
    name: 'CopyProductOptionModal',
    components: { SelectInput, Btn, LoadingSpinner },
    props: {
      modalId: { type: [Number, String], required: true },
      products: { type: Array, default: () => [] },
      currentProductId: { type: String, default: null },
      existingOptionGroups: { type: Array, default: () => [] },
      onSubmit: { type: Function, required: true },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();

      const selectedProductId = ref('');
      const sourceProduct = ref(null);
      const loadingProduct = ref(false);
      const selectedOptionIds = ref([]);
      const selectedGroupIds = ref([]);
      const submitting = ref(false);

      const productOptions = computed(() => {
        const items = (props.products || [])
          .filter((p) => p.id && p.id !== props.currentProductId)
          .filter((p) => (p.options?.length || 0) > 0)
          .map((p) => ({
            value: p.id,
            label: p.name || p.title || p.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'nl'));

        return [
          { value: '', label: $t('products.copy_options.source_product_placeholder') },
          ...items,
        ];
      });

      const sourceOptions = computed(() => sourceProduct.value?.options || []);
      const sourceOptionGroups = computed(
        () => sourceProduct.value?.optionGroups || sourceProduct.value?.option_groups || []
      );

      const ungroupedOptions = computed(() => {
        const groupIds = new Set(sourceOptionGroups.value.map((g) => g.id));
        return sourceOptions.value.filter(
          (opt) => !opt.pageGroupId || !groupIds.has(opt.pageGroupId)
        );
      });

      const groupedOptions = computed(() => {
        const groups = sourceOptionGroups.value.map((group) => ({
          id: group.id,
          name: group.name,
          options: sourceOptions.value.filter((opt) => opt.pageGroupId === group.id),
        }));

        if (ungroupedOptions.value.length) {
          groups.push({
            id: null,
            name: $t('products.copy_options.ungrouped'),
            options: ungroupedOptions.value,
          });
        }

        // Only show groups that have options
        return groups.filter((g) => g.options.length > 0);
      });

      const effectiveSelectedIds = computed(() =>
        resolveSelectedOptionIds({
          sourceOptions: sourceOptions.value,
          selectedOptionIds: selectedOptionIds.value,
          selectedGroupIds: selectedGroupIds.value,
        })
      );

      const selectedCountLabel = computed(() =>
        $t('products.copy_options.selected_count', {
          n: effectiveSelectedIds.value.length,
        })
      );

      const canSubmit = computed(
        () => !loadingProduct.value && !submitting.value && effectiveSelectedIds.value.length > 0
      );

      const submitLabel = computed(() =>
        $t('products.copy_options.submit', { n: effectiveSelectedIds.value.length || 0 })
      );

      const isGroupFullySelected = (group) => {
        if (!group.options.length) return false;
        if (group.id && selectedGroupIds.value.includes(group.id)) return true;
        return group.options.every((opt) => selectedOptionIds.value.includes(opt.id));
      };

      const isGroupPartiallySelected = (group) => {
        if (isGroupFullySelected(group)) return false;
        return group.options.some((opt) => selectedOptionIds.value.includes(opt.id));
      };

      const toggleOption = (optionId, checked) => {
        if (checked) {
          if (!selectedOptionIds.value.includes(optionId)) {
            selectedOptionIds.value = [...selectedOptionIds.value, optionId];
          }
        } else {
          selectedOptionIds.value = selectedOptionIds.value.filter((id) => id !== optionId);
          // If option belonged to a selected group, deselect the group and keep other options
          const option = sourceOptions.value.find((o) => o.id === optionId);
          if (option?.pageGroupId && selectedGroupIds.value.includes(option.pageGroupId)) {
            selectedGroupIds.value = selectedGroupIds.value.filter(
              (id) => id !== option.pageGroupId
            );
            const siblings = sourceOptions.value
              .filter((o) => o.pageGroupId === option.pageGroupId && o.id !== optionId)
              .map((o) => o.id);
            selectedOptionIds.value = [
              ...new Set([...selectedOptionIds.value, ...siblings]),
            ];
          }
        }
      };

      const toggleGroup = (group, checked) => {
        const optionIds = group.options.map((o) => o.id);
        if (checked) {
          if (group.id) {
            if (!selectedGroupIds.value.includes(group.id)) {
              selectedGroupIds.value = [...selectedGroupIds.value, group.id];
            }
          }
          selectedOptionIds.value = [...new Set([...selectedOptionIds.value, ...optionIds])];
        } else {
          if (group.id) {
            selectedGroupIds.value = selectedGroupIds.value.filter((id) => id !== group.id);
          }
          selectedOptionIds.value = selectedOptionIds.value.filter(
            (id) => !optionIds.includes(id)
          );
        }
      };

      const selectAll = () => {
        selectedOptionIds.value = sourceOptions.value.map((o) => o.id);
        selectedGroupIds.value = sourceOptionGroups.value.map((g) => g.id);
      };

      const clearSelection = () => {
        selectedOptionIds.value = [];
        selectedGroupIds.value = [];
      };

      const loadSourceProduct = async (productId) => {
        if (!productId) {
          sourceProduct.value = null;
          return;
        }

        loadingProduct.value = true;
        selectedOptionIds.value = [];
        selectedGroupIds.value = [];

        try {
          // Prefer full product (includes optionGroups); fall back to list item
          const full = await getProductById(productId);
          sourceProduct.value = full;
        } catch (error) {
          const fallback = (props.products || []).find((p) => p.id === productId);
          sourceProduct.value = fallback || null;
        } finally {
          loadingProduct.value = false;
        }
      };

      watch(selectedProductId, (id) => {
        loadSourceProduct(id);
      });

      const handleSubmit = async () => {
        if (!canSubmit.value || !sourceProduct.value) return;

        submitting.value = true;
        try {
          const result = copyProductOptions({
            sourceOptions: sourceOptions.value,
            sourceOptionGroups: sourceOptionGroups.value,
            selectedOptionIds: effectiveSelectedIds.value,
            existingOptionGroups: props.existingOptionGroups || [],
          });

          await props.onSubmit(result);
        } finally {
          submitting.value = false;
        }
      };

      const handleCancel = () => {
        if (submitting.value) return;
        uiStore.closeModal(props.modalId);
      };

      return {
        selectedProductId,
        sourceProduct,
        loadingProduct,
        selectedOptionIds,
        productOptions,
        sourceOptions,
        groupedOptions,
        selectedCountLabel,
        canSubmit,
        submitLabel,
        isGroupFullySelected,
        isGroupPartiallySelected,
        toggleOption,
        toggleGroup,
        selectAll,
        clearSelection,
        handleSubmit,
        handleCancel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .copy-product-option-modal {
    &__intro {
      margin: 0 0 1rem;
      color: var(--slate-600);
      @include fontSize('sm');
    }

    &__loading {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1.5rem 0;
      color: var(--slate-500);
      @include fontSize('sm');
    }

    &__hint {
      margin: 1rem 0 0;
      color: var(--slate-500);
      @include fontSize('sm');
    }

    &__selection {
      margin-top: 1rem;
      max-height: 360px;
      overflow-y: auto;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
    }

    &__toolbar {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border-bottom: 1px solid var(--slate-200);
      background: var(--slate-50);
      position: sticky;
      top: 0;
      z-index: 1;
    }

    &__link {
      background: none;
      border: none;
      padding: 0;
      color: var(--brand-600);
      cursor: pointer;
      @include fontSize('sm');
      font-weight: 500;

      @include hover {
        text-decoration: underline;
      }
    }

    &__count {
      margin-left: auto;
      color: var(--slate-500);
      @include fontSize('sm');
    }

    &__group {
      border-bottom: 1px solid var(--slate-100);

      &:last-child {
        border-bottom: none;
      }
    }

    &__group-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      background: var(--slate-50);
      cursor: pointer;
      font-weight: 600;
      @include fontSize('sm');

      input {
        margin: 0;
      }
    }

    &__group-name {
      flex: 1;
      color: var(--slate-800);
    }

    &__group-count {
      color: var(--slate-400);
      font-weight: 500;
      @include fontSize('sm');
    }

    &__options {
      display: flex;
      flex-direction: column;
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem 0.5rem 1.75rem;
      cursor: pointer;
      @include fontSize('sm');

      @include hover {
        background: var(--slate-50);
      }

      input {
        margin: 0;
      }
    }

    &__option-label {
      flex: 1;
      color: var(--slate-700);
    }

    &__option-type {
      color: var(--slate-400);
      font-family: ui-monospace, monospace;
      @include fontSize('sm');
    }

    &__option-meta {
      color: var(--slate-500);
      @include fontSize('sm');
    }

    .modal__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 1.5rem;
    }
  }
</style>
